/**
 * Which countries have sale pages, as data (web plan MW8): the backend's
 * `locales { hasSaleEvents }`, read once per session through Apollo's cache.
 * The SEO server's route table sends the same query (server/routes.mjs) and
 * hands its answer to the sale routes, so a sale page's first render already
 * knows it.
 *
 * Tiny on purpose: the footer, the home page and the country selector use it
 * on every page. A failed or pending query is "unknown", and `hasSaleEvents`
 * (@/seo/saleCountries) then answers with its fallback (Pakistan only).
 */
import { useContext, useMemo } from 'react';
import { ApolloClient, ApolloLink, InMemoryCache, getApolloContext } from '@apollo/client';
import { SALE_LOCALES } from '@/graphql/queries';
import {
  hasSaleEvents,
  parseSaleCountryFlags,
  saleFlagKnown,
  type BackendLocaleRow,
  type SaleCountryFlags,
} from '@/seo/saleCountries';
import type { Locale } from '@/seo/locales';
import { usePreloadedQuery } from './routePreload';

interface SaleLocalesData {
  locales?: BackendLocaleRow[] | null;
}

export interface SaleCountries {
  /** The parsed flags, or null while unknown (loading, or the query failed). */
  flags: SaleCountryFlags | null;
  /** The query has answered (successfully or not): "no flags" is then final. */
  settled: boolean;
}

/**
 * A client that never sends anything, so the hook below can stay one
 * unconditional `useQuery` in a tree with no ApolloProvider (a bare router in a
 * test, a page outside the providers): the query is skipped there and the flags
 * are unknown.
 */
let inertClient: ApolloClient<unknown> | null = null;
function inert(): ApolloClient<unknown> {
  inertClient ??= new ApolloClient({ cache: new InMemoryCache(), link: ApolloLink.empty() });
  return inertClient;
}

/** The flags of every launched country. */
export function useSaleCountries(): SaleCountries {
  const { client } = useContext(getApolloContext());
  const result = usePreloadedQuery<SaleLocalesData>(SALE_LOCALES, {
    errorPolicy: 'all',
    skip: !client,
    client: client ?? inert(),
  });
  const rows = result.data?.locales;
  const flags = useMemo(() => parseSaleCountryFlags(rows), [rows]);
  return { flags, settled: !result.loading };
}

/** Whether `locale`'s country has sale pages (the fallback until the answer is known). */
export function useHasSaleEvents(locale: Pick<Locale, 'cc'>): boolean {
  const { flags } = useSaleCountries();
  return hasSaleEvents(locale, flags);
}

export interface SaleCountryState {
  /** The country has sale pages. */
  enabled: boolean;
  /**
   * The answer is final: known from the backend, or the query settled without
   * one (then `enabled` is the fallback). A page that would answer 404 waits
   * for it instead of flashing the 404 page at a country whose flag is on its way.
   */
  ready: boolean;
}

/** `useHasSaleEvents` plus whether the answer can still change (for the sale pages' 404). */
export function useSaleCountry(locale: Pick<Locale, 'cc'>): SaleCountryState {
  const { flags, settled } = useSaleCountries();
  return { enabled: hasSaleEvents(locale, flags), ready: settled || saleFlagKnown(locale, flags) };
}
